import React, {memo} from 'react';
import DialogComponent from 'components/Dialog';
import {DialogTypes} from 'common/names';
import {GridColDef, DataGridPro} from '@mui/x-data-grid-pro';
import {formatDateTime} from 'common/dateUtils';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import {IScheduledReportsParameters} from 'types/diplomat/reports/ScheduledReportsParameters';
import {Box, Grid, IconButton} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {useTranslation} from 'react-i18next';
import {ConfirmationDialog} from 'components/dialogs/ConfirmationDialog';
import {useDeleteApi} from 'common/hooks/useDeleteApi';
import AddEditScheduledReport from './AddEditScheduledReport';
import {usePutApi} from 'common/hooks/usePutApi';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {useToggle} from 'common/hooks/useToggle';
import {usePostApi} from 'common/hooks/usePostApi';
import Button from 'components/Button';
import {ReportType} from 'types/diplomat/reports/ReportType';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';

type Props = {
   onClose: () => void;
};
const ScheduledReports = memo(({onClose}: Props) => {
   const {t} = useTranslation();
   // TODO update type
   const {request, state: scheduledReports} = useGetApi<IScheduledReportsParameters[]>(
      api.scheduleReports
   );

   React.useEffect(() => {
      request();
   }, [request]);

   const [toDelete, setToDelete] = React.useState<IScheduledReportsParameters | null>();
   const [toEdit, setToEdit] = React.useState<IScheduledReportsParameters | null>();
   const [isAdding, toggleAdd] = useToggle();

   const {request: removeScheduledReport} = useDeleteApi(`${api.scheduleReports}/${toDelete?.id}`, {
      onSuccess: () => {
         setToDelete(null);
         request();
      },
   });

   const {request: _updateSchedule} = usePutApi(`${api.scheduleReports}/${toEdit?.id}`);
   const updateSchedule = React.useCallback(
      (
         values: IScheduledReportsParameters,
         formikProps: FormOnSubmitHelpers<IScheduledReportsParameters>
      ) => {
         _updateSchedule(JSON.stringify(values), null, {
            onSuccess: () => {
               setToEdit(null);
               request();
            },
            onFailure: (err) => {
               formikProps.setSubmitting(false);
               formikProps.setErrors(err);
            },
         });
      },
      [_updateSchedule, request]
   );

   const {request: _addSchedule} = usePostApi(api.scheduleReports);
   const addSchedule = React.useCallback(
      (
         values: IScheduledReportsParameters,
         formikProps: FormOnSubmitHelpers<IScheduledReportsParameters>
      ) => {
         _addSchedule(JSON.stringify(values), {
            onSuccess: () => {
               toggleAdd();
               request();
            },
            onFailure: (err) => {
               formikProps.setSubmitting(false);
               formikProps.setErrors(err);
            },
         });
      },
      [_addSchedule, toggleAdd, request]
   );

   const cols: GridColDef[] = React.useMemo(
      () => [
         {
            field: 'type',
            align: 'center',
            headerAlign: 'center',
            headerName: t('Report Type'),
            flex: 200,
            valueFormatter: (params) => {
               return ReportType[params?.value as keyof typeof ReportType.enum];
            },
         },
         {
            field: 'name',
            align: 'center',
            headerAlign: 'center',
            headerName: t('Report Name'),
            flex: 300,
         },
         {
            field: 'nextExecution',
            align: 'center',
            headerAlign: 'center',
            headerName: t('Next Scheduled Report'),
            flex: 200,
            valueFormatter: (params) => {
               return formatDateTime(params?.value as Date);
            },
         },
         {
            field: '_',
            align: 'center',
            headerAlign: 'center',
            headerName: ' ',
            sortable: false,
            filterable: false,
            renderCell: (params) => {
               return (
                  <Box>
                     <IconButton
                        size="small"
                        onClick={() => setToEdit(params.row as IScheduledReportsParameters)}
                     >
                        <EditOutlinedIcon />
                     </IconButton>

                     <IconButton
                        size="small"
                        onClick={() => setToDelete(params.row as IScheduledReportsParameters)}
                     >
                        <DeleteOutlineOutlinedIcon />
                     </IconButton>
                  </Box>
               );
            },
         },
      ],
      [t]
   );

   return (
      <>
         <DialogComponent
            maxWidth="md"
            header={DialogTypes.ScheduledReports.dialogName}
            close={onClose}
         >
            <div style={{height: 400, width: '100%', marginBottom: '.5rem'}}>
               <DataGridPro
                  density="compact"
                  rows={scheduledReports || []}
                  columns={cols}
                  pageSize={8}
                  //    onRowSelected={(param) => setSelected(param.data)}
               />
            </div>
            <Grid container spacing={3}>
               <Grid item>
                  <PrimaryButton onClick={toggleAdd}>{t('Add Report')}</PrimaryButton>
               </Grid>
            </Grid>
            <Grid container spacing={3} justifyContent="center">
               <Grid item>
                  <Button type="button" onClick={onClose}>
                     {t('Close')}
                  </Button>
               </Grid>
            </Grid>
         </DialogComponent>
         <ConfirmationDialog
            open={!!toDelete}
            content={t('Do you want to delete the Scheduled Report named "{{name}}"', {
               name: toDelete?.name,
            })}
            onOk={removeScheduledReport}
            onCancel={() => setToDelete(null)}
         />
         {toEdit && (
            <AddEditScheduledReport
               scheduledReport={toEdit}
               onClose={() => setToEdit(null)}
               onSubmit={updateSchedule}
            />
         )}
         {isAdding && <AddEditScheduledReport onClose={toggleAdd} onSubmit={addSchedule} />}
      </>
   );
});

export default ScheduledReports;
